import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';
import { shouldHideResolvedByDefault } from 'dashboard/helper/conversationLayoutHelper';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const getConversationAccessContext = (rootGetters = {}) => {
  const currentUser = rootGetters.getCurrentUser || {};
  const currentAccountId =
    rootGetters.getCurrentAccountId || rootGetters.getCurrentAccount?.id;

  return {
    currentUserId: currentUser.id,
    role: getUserRole(currentUser, currentAccountId),
    permissions: getUserPermissions(currentUser, currentAccountId),
  };
};

const canBrowseAccountConversations = (rootGetters = {}) => {
  const { role, permissions } = getConversationAccessContext(rootGetters);
  return (
    role === 'administrator' || permissions.includes('conversation_manage')
  );
};

// A team leader always has visibility over conversations of the team they
// lead, regardless of assignment / participation / inbox restrictions.
const isLeaderOfConversationTeam = conversation => {
  const { team } = conversation.meta || {};
  return Boolean(team?.is_leader);
};

const isVisibleTeamConversation = conversation => {
  const { assignee, team } = conversation.meta || {};

  return (
    Boolean(team?.is_member) && (!assignee || !team?.restrict_to_own_messages)
  );
};

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: ({
    allConversations,
    chatSortFilter,
    appliedFilters,
  }) => {
    return allConversations
      .filter(conversation => matchesFilters(conversation, appliedFilters))
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmailWithContent = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isPublic = !message.private;
      const isIncomingOrOutgoing = [
        MESSAGE_TYPE.OUTGOING,
        MESSAGE_TYPE.INCOMING,
      ].includes(messageType);
      return email.from && isIncomingOrOutgoing && isPublic;
    });

    if (lastEmailWithContent) {
      return lastEmailWithContent;
    }

    return [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const { currentUserId } = getConversationAccessContext(rootGetters);
    const canBrowseAllConversations =
      canBrowseAccountConversations(rootGetters);

    return _state.allConversations.filter(conversation => {
      const { assignee, is_participating: isParticipating } =
        conversation.meta || {};
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isAdminView = canBrowseAllConversations && Boolean(assignee);
      const isAgentView =
        !canBrowseAllConversations &&
        (assignee?.id === currentUserId ||
          Boolean(isParticipating) ||
          isLeaderOfConversationTeam(conversation));

      return (isAdminView || isAgentView) && shouldFilter;
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    const { currentUserId } = getConversationAccessContext(rootGetters);
    const canBrowseAllConversations =
      canBrowseAccountConversations(rootGetters);

    return _state.allConversations.filter(conversation => {
      if (
        shouldHideResolvedByDefault(activeFilters) &&
        conversation.status === 'resolved'
      ) {
        return false;
      }

      const { assignee, team } = conversation.meta || {};
      const isPending = conversation.status === 'pending';
      const isUnassigned = !assignee;
      const isAssignedToMe = assignee?.id === currentUserId;
      const isAssignedToTeam = Boolean(team?.is_member) && isUnassigned;
      const isLeaderOfTeam = isLeaderOfConversationTeam(conversation);

      if (canBrowseAllConversations) {
        return isPending && applyPageFilters(conversation, activeFilters);
      }

      return (
        (isAssignedToMe || isAssignedToTeam || isLeaderOfTeam) &&
        isPending &&
        applyPageFilters(conversation, activeFilters)
      );
    });
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const { currentUserId, role, permissions } =
      getConversationAccessContext(rootGetters);
    const canBrowseAllConversations =
      canBrowseAccountConversations(rootGetters);

    return _state.allConversations.filter(conversation => {
      if (
        shouldHideResolvedByDefault(activeFilters) &&
        conversation.status === 'resolved'
      ) {
        return false;
      }

      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const { assignee, is_participating: isParticipating } =
        conversation.meta || {};

      if (canBrowseAllConversations) {
        return shouldFilter;
      }

      if (role === 'custom_role') {
        const hasAccess =
          applyRoleFilter(conversation, role, permissions, currentUserId) ||
          isLeaderOfConversationTeam(conversation);

        return hasAccess && shouldFilter;
      }

      const isAssignedToMe = assignee?.id === currentUserId;
      const isLeaderOfTeam = isLeaderOfConversationTeam(conversation);
      const isVisibleTeamScopedConversation =
        isVisibleTeamConversation(conversation);
      return (
        (isAssignedToMe ||
          Boolean(isParticipating) ||
          isVisibleTeamScopedConversation ||
          isLeaderOfTeam) &&
        shouldFilter
      );
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getPinnedMessagesByConversation: _state => {
    return _state.pinnedMessagesByConversation;
  },

  getSelectedChatPinnedMessages: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    if (!selectedChat?.id) return [];
    return _state.pinnedMessagesByConversation[selectedChat.id] || [];
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
